import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Truck, Plus, ChevronRight, Search, Loader2, X,
  Bus, Tent, Zap, Mountain, Droplets, Sun, ShowerHead, Car,
  Caravan, Container, MapPin, CheckCircle2, Smartphone, ExternalLink
} from 'lucide-react';
import { AppUser, VehicleCategory, VehicleEquipment } from '../types';
import { addVehicle, fetchVehicleDataFromSVV } from '../services/vehicleService';
import { updateUserSettings, fetchSystemConfig } from '../services/firebaseService';
import { isAdminUser } from '../services/accessControl';
import { colorClasses } from '../utils/tailwindColorClasses';

// ── Reuse same config from GarageTab ────────────────────────────────

const CATEGORY_META: Record<VehicleCategory, { icon: React.ElementType; color: string }> = {
  motorhome:      { icon: Truck,     color: 'blue' },
  campervan:      { icon: Bus,       color: 'purple' },
  caravan:        { icon: Caravan,   color: 'teal' },
  camper_trailer: { icon: Container, color: 'rose' },
  rooftent:       { icon: Tent,      color: 'orange' },
  offroad:        { icon: Mountain,  color: 'lime' },
  car:            { icon: Car,       color: 'slate' },
};

const CATEGORIES: VehicleCategory[] = ['motorhome', 'campervan', 'caravan', 'camper_trailer', 'rooftent', 'offroad', 'car'];

const EQUIPMENT_KEYS: { key: keyof VehicleEquipment; icon: React.ElementType; color: string; motorOnly?: boolean }[] = [
  { key: 'is_ev',             icon: Zap,        color: 'blue',   motorOnly: true },
  { key: 'four_wheel_drive',  icon: Mountain,   color: 'amber',  motorOnly: true },
  { key: 'fixed_toilet',      icon: ShowerHead, color: 'violet' },
  { key: 'grey_water_tank',   icon: Droplets,   color: 'cyan' },
  { key: 'solar_panel',       icon: Sun,        color: 'yellow' },
];

const TRAILER_CATEGORIES: VehicleCategory[] = ['caravan', 'camper_trailer'];

const DEFAULT_EQUIPMENT: VehicleEquipment = {
  is_ev: false, four_wheel_drive: false, fixed_toilet: false,
  grey_water_tank: false, solar_panel: false,
};

// ── Props ────────────────────────────────────────────────────────────

interface VehicleOnboardingOverlayProps {
  user: AppUser;
  onComplete: () => void;
}

// ── Component ────────────────────────────────────────────────────────

const VehicleOnboardingOverlay: React.FC<VehicleOnboardingOverlayProps> = ({ user, onComplete }) => {
  const { t } = useTranslation();

  // Form state
  const [nickname, setNickname] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [category, setCategory] = useState<VehicleCategory>('motorhome');
  const [lengthCm, setLengthCm] = useState('');
  const [widthCm, setWidthCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [equipment, setEquipment] = useState<VehicleEquipment>({ ...DEFAULT_EQUIPMENT });
  const [regNr, setRegNr] = useState('');
  const [markerColor, setMarkerColor] = useState('');

  // UI state
  const [completed, setCompleted] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [svvLoading, setSvvLoading] = useState(false);
  const [svvError, setSvvError] = useState<string | null>(null);
  const [svvSuccess, setSvvSuccess] = useState(false);
  const [svvExpanded, setSvvExpanded] = useState(false);
  const [testflightOptIn, setTestflightOptIn] = useState(false);
  const [testflightSaving, setTestflightSaving] = useState(false);
  const [testflightVisible, setTestflightVisible] = useState(false);
  useEffect(() => {
    const isAdmin = isAdminUser(user);
    if (isAdmin) { setTestflightVisible(true); return; }
    fetchSystemConfig('testflight_sync').then(cfg => {
      if (cfg?.public) setTestflightVisible(true);
    });
  }, [user]);

  const toggleEquipment = (key: keyof VehicleEquipment) => {
    setEquipment(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSVVLookup = async () => {
    const cleaned = regNr.toUpperCase().replace(/\s/g, '');
    if (!cleaned) return;
    setSvvLoading(true);
    setSvvError(null);
    setSvvSuccess(false);
    try {
      const result = await fetchVehicleDataFromSVV(cleaned);
      if (result.ok) {
        if (result.make_model) setMakeModel(result.make_model);
        if (result.length_cm) setLengthCm(result.length_cm.toString());
        if (result.width_cm) setWidthCm(result.width_cm.toString());
        if (result.weight_kg) setWeightKg(result.weight_kg.toString());
        if (result.is_ev) setEquipment(prev => ({ ...prev, is_ev: true }));
        if (result.is_4wd) setEquipment(prev => ({ ...prev, four_wheel_drive: true }));
        if (result.suggested_category) setCategory(result.suggested_category);
        if (result.color) setMarkerColor(result.color);
        setRegNr(cleaned);
        setSvvSuccess(true);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (code.includes('not-found')) {
        setSvvError(t('profile.garage.reg_not_found'));
      } else {
        setSvvError(t('profile.garage.reg_lookup_error'));
      }
    } finally {
      setSvvLoading(false);
    }
  };

  const handleSave = async () => {
    if (!nickname.trim()) return;
    setSaving(true);
    try {
      await addVehicle(user.uid, {
        nickname: nickname.trim(),
        make_model: makeModel.trim(),
        registration_number: regNr.trim() || undefined,
        category,
        length_cm: lengthCm ? parseInt(lengthCm, 10) : undefined,
        width_cm: widthCm ? parseInt(widthCm, 10) : undefined,
        weight_kg: weightKg ? parseInt(weightKg, 10) : undefined,
        equipment,
        is_default: true,
        created_at: Date.now(),
        marker_color: markerColor || undefined,
      });
      await updateUserSettings(user.uid, { onboarding_vehicle_done: true });
      setSaving(false);
      setCompleted(true);
    } catch (err) {
      console.error('Failed to save vehicle during onboarding:', err);
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await updateUserSettings(user.uid, { onboarding_vehicle_done: true });
      setSaving(false);
      setSkipped(true);
      setCompleted(true);
    } catch (err) {
      console.error('Failed to skip vehicle onboarding:', err);
      setSaving(false);
    }
  };

  const handleTestFlightOptIn = async (value: boolean) => {
    setTestflightOptIn(value);
    setTestflightSaving(true);
    try {
      await updateUserSettings(user.uid, {
        testflight_opt_in: value,
        testflight_opt_in_at: value ? new Date() : null,
      });
    } catch (e) { console.error('TestFlight opt-in failed:', e); }
    finally { setTestflightSaving(false); }
  };


  const inputClass = 'w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-4 py-2.5 text-[var(--text-primary)] text-[13px] font-bold focus:border-emerald-500/30 outline-none transition-all placeholder:text-[var(--text-muted)]';

  if (completed) {
    return (
      <div className="fixed left-0 right-0 bottom-0 z-[10010] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
        <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" />
        <div className="relative w-full max-w-lg bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-modal shadow-3xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
          <div className="p-8 md:p-10 text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 mx-auto">
              <CheckCircle2 size={40} className="text-emerald-400" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tight">
                {t('profile.garage.onboarding_complete_title')}
              </h2>
              <p className="text-[12px] text-[var(--text-secondary)] font-bold leading-relaxed max-w-xs mx-auto">
                {skipped
                  ? t('profile.garage.onboarding_complete_skipped_desc')
                  : t('profile.garage.onboarding_complete_desc')
                }
              </p>
            </div>
            {/* TestFlight opt-in */}
            {user.email && testflightVisible && (
              <div className="bg-blue-500/5 border border-blue-500/15 rounded-2xl p-4 space-y-2 text-left">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Smartphone size={16} className="text-blue-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest">
                        {t('onboarding.testflight_title', 'Test iOS-appen')}
                      </p>
                      <p className="text-2xs text-[var(--text-muted)] leading-relaxed">
                        {t('onboarding.testflight_desc', 'Meld deg på for iOS-testing via TestFlight')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleTestFlightOptIn(!testflightOptIn)}
                    disabled={testflightSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                      testflightOptIn ? 'bg-blue-600' : 'bg-[var(--bg-input)]'
                    } ${testflightSaving ? 'opacity-50' : ''}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      testflightOptIn ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {testflightOptIn && (
                  <p className="text-2xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 size={10} />
                    {t('onboarding.testflight_confirmed', 'Instruks sendes på epost')}
                  </p>
                )}
              </div>
            )}
            {/* Google Play Android beta */}
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-2xl p-4 space-y-3 text-left">
                <div className="flex items-center gap-2.5">
                  <Smartphone size={16} className="text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest">
                      {t('onboarding.googleplay_title', 'Test Android-appen')}
                    </p>
                    <p className="text-2xs text-[var(--text-muted)] leading-relaxed">
                      {t('onboarding.googleplay_desc', 'Bli med i testgruppen og last ned beta-appen fra Google Play')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://groups.google.com/g/fricamp-testere"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-600/30 text-emerald-300 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95"
                  >
                    <ExternalLink size={11} />
                    {t('onboarding.googleplay_join_group', 'Bli med i testgruppen')}
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.takleconsulting.fricamp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-[var(--text-secondary)] py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95"
                  >
                    <ExternalLink size={11} />
                    {t('onboarding.googleplay_find_app', 'Finn appen på Google Play')}
                  </a>
                </div>
            </div>
            <button
              onClick={onComplete}
              className="w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl text-tiny font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white transition-all active:scale-[0.98]"
            >
              <MapPin size={18} />
              {t('profile.garage.onboarding_explore')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10010] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" />
      <div className="relative w-full max-w-lg bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-modal shadow-3xl overflow-hidden flex flex-col max-h-[90dvh] animate-in zoom-in-95 duration-300" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-5 right-5 z-50 p-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-full transition-all"
        >
          <X size={18} />
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-5" style={{ overscrollBehavior: 'contain' }}>

          {/* Header */}
          <div className="text-center space-y-3 pt-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/20 mx-auto">
              <Truck size={30} className="text-orange-400" />
            </div>
            <h2 className="text-xl font-black text-[var(--text-primary)] tracking-tight">
              {t('profile.garage.onboarding_title')}
            </h2>
            <p className="text-tiny text-[var(--text-secondary)] font-bold leading-relaxed max-w-sm mx-auto">
              {t('profile.garage.onboarding_subtitle')}
            </p>
          </div>

          {/* SVV Lookup — Norwegian flag button, expandable */}
          {!svvExpanded ? (
            <button
              type="button"
              onClick={() => setSvvExpanded(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/5 border border-blue-500/15 hover:bg-blue-500/10 transition-all"
            >
              <span className="text-base leading-none">🇳🇴</span>
              <Search size={13} className="text-blue-400" />
              <span className="text-3xs font-black text-blue-400 uppercase tracking-widest">{t('profile.garage.reg_lookup')}</span>
            </button>
          ) : (
            <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-base leading-none">🇳🇴</span>
                  <p className="text-3xs font-black text-blue-400 uppercase tracking-widest">
                    {t('profile.garage.reg_lookup')}
                  </p>
                </div>
                <button onClick={() => setSvvExpanded(false)} className="p-1 rounded-md hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all">
                  <X size={12} />
                </button>
              </div>
              <p className="text-4xs text-[var(--text-muted)] font-bold">{t('profile.garage.reg_norway_only')}</p>
              <div className="flex gap-2">
                <input
                  value={regNr}
                  onChange={e => setRegNr(e.target.value.toUpperCase())}
                  placeholder={t('profile.garage.reg_placeholder')}
                  className="flex-1 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-lg px-3 py-2 text-[var(--text-primary)] text-[13px] font-bold tracking-wider focus:border-blue-500/30 outline-none transition-all placeholder:text-[var(--text-muted)] uppercase"
                  maxLength={8}
                />
                <button
                  onClick={handleSVVLookup}
                  disabled={svvLoading || !regNr.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-2xs font-black uppercase tracking-widest transition-all"
                >
                  {svvLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  {svvLoading ? t('profile.garage.reg_lookup_loading') : t('profile.garage.reg_lookup_short')}
                </button>
              </div>
              {svvError && <p className="text-3xs font-bold text-red-400">{svvError}</p>}
              {svvSuccess && <p className="text-3xs font-bold text-emerald-400">{t('profile.garage.reg_filled')}</p>}
            </div>
          )}

          {/* Nickname */}
          <div>
            <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
              {t('profile.garage.nickname')} *
            </label>
            <input
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder={t('profile.garage.nickname_placeholder')}
              className={inputClass}
              maxLength={40}
            />
          </div>

          {/* Make / Model */}
          <div>
            <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
              {t('profile.garage.make_model')}
            </label>
            <input
              value={makeModel}
              onChange={e => setMakeModel(e.target.value)}
              placeholder={t('profile.garage.make_model_placeholder')}
              className={inputClass}
              maxLength={60}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
              {t('profile.garage.category')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIES.map(cat => {
                const meta = CATEGORY_META[cat];
                const Icon = meta.icon;
                const isSelected = category === cat;
                const c = colorClasses(meta.color);
                return (
                  <button
                    key={cat}
                    onClick={() => {
                      setCategory(cat);
                      if (TRAILER_CATEGORIES.includes(cat)) {
                        setEquipment(prev => ({ ...prev, is_ev: false, four_wheel_drive: false }));
                      }
                    }}
                    className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl text-4xs font-black uppercase tracking-widest transition-all border text-center ${
                      isSelected
                        ? `${c.bg15} ${c.text400} ${c.border30}`
                        : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    <Icon size={14} />
                    {t(`profile.garage.categories.${cat}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dimensions & Weight */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
                {t('profile.garage.length_cm')}
              </label>
              <input
                type="number"
                value={lengthCm}
                onChange={e => setLengthCm(e.target.value)}
                placeholder="700"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
                {t('profile.garage.width_cm')}
              </label>
              <input
                type="number"
                value={widthCm}
                onChange={e => setWidthCm(e.target.value)}
                placeholder="230"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
                {t('profile.garage.weight_kg')}
              </label>
              <input
                type="number"
                value={weightKg}
                onChange={e => setWeightKg(e.target.value)}
                placeholder="3500"
                className={inputClass}
              />
            </div>
          </div>

          {/* Equipment toggles */}
          <div>
            <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">
              {t('profile.garage.equipment')}
            </label>
            <div className="space-y-1.5">
              {EQUIPMENT_KEYS.filter(eq => !eq.motorOnly || !TRAILER_CATEGORIES.includes(category)).map(eq => {
                const EqIcon = eq.icon;
                const isOn = equipment[eq.key];
                const ec = colorClasses(eq.color);
                return (
                  <div
                    key={eq.key}
                    className="bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2.5 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2.5">
                      <EqIcon size={14} className={ec.text400} />
                      <span className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        {t(`profile.garage.${eq.key}`)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleEquipment(eq.key)}
                      className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${
                        isOn ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${isOn ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Fixed bottom actions */}
        <div className="p-6 pt-3 space-y-3 border-t border-[var(--border-primary)] bg-[var(--bg-base)]">
          <button
            onClick={handleSave}
            disabled={saving || !nickname.trim()}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-tiny font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white transition-all active:scale-[0.98]"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {saving ? t('profile.garage.saving') : t('profile.garage.save')}
          </button>
          <button
            onClick={handleSkip}
            disabled={saving}
            className="w-full py-2.5 text-2xs font-bold text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-all disabled:opacity-50"
          >
            {t('profile.garage.onboarding_skip')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleOnboardingOverlay;
